import { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

import FnrShineButton from "Utilities/FnrShineButton";
import useWizard from "../useWizard";
import { ACCENT_COLOR } from "../theme";

function formatDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const months = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];
  return `${months[m - 1]} ${d}, ${y}`;
}

function joinWords(words) {
  if (words.length === 0) return "";
  if (words.length === 1) return words[0];
  if (words.length === 2) return `${words[0]} and ${words[1]}`;
  return words.slice(0, -1).join(", ") + ", and " + words[words.length - 1];
}

function useDetailSections() {
  const { data } = useWizard();
  const info = data.studentInfo;
  const perm = data.permissionRelease;
  const pricing = data.pricing;
  const gen = data.generalInfo;

  const sections = [];

  // Program Info
  const classesList = (gen.classes || []).filter((c) => c.trim());
  let programSummary = "";
  if (gen.programName && classesList.length > 0) {
    programSummary = `Your program "${gen.programName}" has ${classesList.length} class${classesList.length > 1 ? "es" : ""}: ${joinWords(classesList)}`;
    if (gen.registrationDeadline) {
      programSummary += `, with registration closing ${formatDate(gen.registrationDeadline)}`;
    }
    programSummary += ".";
  } else if (gen.programName) {
    programSummary = `Your program "${gen.programName}"`;
    if (gen.registrationDeadline) {
      programSummary += ` has registration closing ${formatDate(gen.registrationDeadline)}`;
    }
    programSummary += ".";
  }
  if (programSummary) {
    sections.push({
      icon: "fa-school",
      title: "Program Info",
      summary: programSummary,
      editStep: 1,
      editSubStep: 0,
    });
  }

  // Student Info — family
  const familyParts = ["name", "email", "phone number"];
  if (info.address) familyParts.push("home address");
  const studentSentences = [];
  studentSentences.push(
    `We'll collect each student's name along with their parent or guardian's ${joinWords(familyParts)}.`
  );

  // Student details
  const details = [];
  if (info.birthday) details.push("birthday");
  if (info.gender) details.push("gender");
  if (info.school) details.push("school");
  if (info.grade) details.push("grade");
  if (info.shirtSize) details.push("shirt size");
  if (details.length > 0) {
    studentSentences.push(`We'll also ask for each student's ${joinWords(details)}.`);
  }

  sections.push({
    icon: "fa-id-card",
    title: "Student Details",
    summary: studentSentences.join(" "),
    editStep: 3,
    editSubStep: 0,
  });

  // Baptism
  if (info.baptism) {
    sections.push({
      icon: "fa-water",
      title: "Baptism",
      summary: "We'll ask if the student has been baptized and give them the option to upload a certificate.",
      editStep: 3,
      editSubStep: 1,
    });
  }

  // Permission Forms — only show if at least one is not declined
  const releases = [];
  if (perm.generalReleaseCustom && !perm.generalReleaseDeclined) releases.push("general permission release");
  if (perm.photoReleaseCustom && !perm.photoReleaseDeclined) releases.push("photo release");
  if (releases.length > 0) {
    sections.push({
      icon: "fa-clipboard-check",
      title: "Permission Forms",
      summary: `Families will be asked to agree to your ${joinWords(releases)}.`,
      editStep: 2,
      editSubStep: 0,
    });
  }

  // Medical & Emergency
  const medParts = [];
  if (info.allergiesMedical) medParts.push("any allergies or medical conditions");
  if (info.emergencyContacts) medParts.push("an additional emergency contact");
  if (medParts.length > 0) {
    sections.push({
      icon: "fa-heart-pulse",
      title: "Medical & Emergency",
      summary: `We'll ask families to provide ${joinWords(medParts)} for their students.`,
      editStep: 3,
      editSubStep: 2,
    });
  }

  // More Info
  const moreParts = [];
  if (info.previousParish) moreParts.push("previous parish");
  if (info.preferredLanguage) moreParts.push("preferred language");
  if (info.godparents) moreParts.push("godparents/sponsors");
  if (info.siblings) moreParts.push("siblings in program");
  if (info.specialNeeds) moreParts.push("special needs/accommodations");
  if (info.parentVolunteer) moreParts.push("parent volunteer interest");
  if (info.massAttendance) moreParts.push("mass attendance");
  if (info.transportationNeeds) moreParts.push("transportation needs");
  const customFieldLabels = (info.customFields || [])
    .filter((f) => f.label.trim())
    .map((f) => f.label);
  moreParts.push(...customFieldLabels);
  if (moreParts.length > 0) {
    sections.push({
      icon: "fa-pen-field",
      title: "More Info",
      summary: `We'll also ask about ${joinWords(moreParts)}.`,
      editStep: 3,
      editSubStep: 4,
    });
  }

  // Pricing
  const pricingSentences = [];
  if (pricing.baseFee) {
    let base = `Students will be charged $${pricing.baseFee} per student`;
    if (pricing.familyCap && pricing.familyCapValue) {
      base += ` with a family cap of $${pricing.familyCapValue}`;
    }
    base += ".";
    pricingSentences.push(base);
  }
  if (pricing.requirePayment) {
    pricingSentences.push("Payment will be required to complete registration.");
  }

  // Additional fees
  const fees = pricing.additionalFees || {};
  const allClasses = gen.classes || [];
  const feeEntries = Object.entries(fees).filter(([, v]) => v);
  if (feeEntries.length > 0) {
    const feeStrs = feeEntries.map(
      ([idx, val]) => `${allClasses[idx] || `Class ${parseInt(idx) + 1}`} ($${val})`
    );
    pricingSentences.push(`Additional fees will apply for ${joinWords(feeStrs)}.`);
  }

  // Early/late
  if (pricing.earlyBirdPricing && pricing.earlyBirdFee) {
    let str = `Early bird pricing of $${pricing.earlyBirdFee} per student`;
    if (pricing.earlyBirdDate) str += ` is available before ${formatDate(pricing.earlyBirdDate)}`;
    pricingSentences.push(str + ".");
  }
  if (pricing.latePricing && pricing.lateFee) {
    let str = `A late fee of $${pricing.lateFee} per student`;
    if (pricing.lateDate) str += ` will apply after ${formatDate(pricing.lateDate)}`;
    pricingSentences.push(str + ".");
  }

  // Member/catechist
  if (pricing.memberPricing && pricing.memberFee) {
    pricingSentences.push(`Parish members will pay $${pricing.memberFee} per student.`);
  }
  if (pricing.catechistPricing && pricing.catechistFee) {
    pricingSentences.push(`Catechists will pay $${pricing.catechistFee} per student.`);
  }

  if (pricingSentences.length > 0) {
    sections.push({
      icon: "fa-dollar-sign",
      title: "Pricing",
      summary: pricingSentences.join(" "),
      editStep: 4,
      editSubStep: 0,
    });
  }

  return sections;
}

const FEATURES = [
  {
    icon: "fa-file-signature",
    label: "Custom Registration",
    desc: "We'll set up a custom family registration and signup for your RE program. Your members will first make sure their family data is up-to-date, and then parents can sign up their children.",
  },
  {
    icon: "fa-layer-group",
    label: "Automatic Groups",
    desc: "We'll create groups that your students and parents are automatically added to when they sign up.",
  },
  {
    icon: "fa-chart-bar",
    label: "Pre-Built Reports",
    desc: "We'll pre-fill your groups with reports you can use to run your RE program.",
  },
];

function ConfirmationView() {
  const { goToStep } = useWizard();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const sections = useDetailSections();
  const detailsRef = useRef(null);
  const innerRef = useRef(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);

  useEffect(() => {
    if (!innerRef.current) return;
    const ro = new ResizeObserver(() => {
      if (innerRef.current) setMeasuredHeight(innerRef.current.scrollHeight);
    });
    ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <Content>
      <Title className="merri">You're all set!</Title>

      <CardsCenter>
        <FeatureCards>
          {FEATURES.map((f) => (
            <FeatureCard key={f.label}>
              <FeatureIconWrap>
                <FeatureIcon className={`fas ${f.icon}`} />
              </FeatureIconWrap>
              <FeatureTextWrap>
                <FeatureLabel>{f.label}</FeatureLabel>
                <FeatureDesc className="raleway">{f.desc}</FeatureDesc>
              </FeatureTextWrap>
            </FeatureCard>
          ))}
        </FeatureCards>
      </CardsCenter>

      <DetailsWrap>
        <DetailsToggle onClick={() => {
          setDetailsOpen((o) => {
            if (!o) {
              setTimeout(() => {
                detailsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 320);
            }
            return !o;
          });
        }}>
          <DetailsToggleInner>
            <DetailsChevron
              className="fas fa-chevron-right"
              $open={detailsOpen}
            />
            Review Details
          </DetailsToggleInner>
        </DetailsToggle>

        <DetailsClip $height={detailsOpen ? measuredHeight : 0}>
          <div ref={innerRef}>
            <DetailsList ref={detailsRef}>
              {sections.map((section) => (
                <DetailRow key={section.title}>
                  <DetailIconWrap>
                    <DetailIcon className={`fas ${section.icon}`} />
                  </DetailIconWrap>
                  <DetailText>
                    <DetailTitle>{section.title}</DetailTitle>
                    <DetailSummary>{section.summary}</DetailSummary>
                  </DetailText>
                  <EditLink
                    onClick={() =>
                      goToStep(section.editStep, section.editSubStep)
                    }
                  >
                    <EditLinkInner>
                      <i className="fas fa-pen" /> Edit
                    </EditLinkInner>
                  </EditLink>
                </DetailRow>
              ))}
            </DetailsList>
          </div>
        </DetailsClip>
      </DetailsWrap>
    </Content>
  );
}

/* ── Loading View ────────────────────────────────────────── */

const LOADING_STEPS = [
  { icon: "fa-file-signature", label: "Building your family forms" },
  { icon: "fa-clipboard-list", label: "Creating your signup" },
  { icon: "fa-list-check", label: "Applying your fields" },
  { icon: "fa-layer-group", label: "Setting up your groups" },
  { icon: "fa-chart-bar", label: "Preparing your reports" },
  {
    icon: "fa-hands-praying",
    label: "Saying a prayer",
    subtext: "Yep \u2014 really! We've let our team know to pray for your RE program.",
  },
];

// Cumulative times (ms) at which each step finishes
const STEP_DURATIONS = [1000, 1000, 1000, 1000, 1000, 3000];
const STEP_DONE_AT = STEP_DURATIONS.reduce((acc, d, i) => {
  acc.push((acc[i - 1] || 0) + d);
  return acc;
}, []);
const NAVIGATE_AT = STEP_DONE_AT[STEP_DONE_AT.length - 1] + 1000; // 8s total

function LoadingView() {
  const { goToStep } = useWizard();
  const [doneCount, setDoneCount] = useState(0);

  useEffect(() => {
    const timers = STEP_DONE_AT.map((t, i) =>
      setTimeout(() => setDoneCount(i + 1), t)
    );
    const navTimer = setTimeout(() => goToStep(5, 2), NAVIGATE_AT);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(navTimer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LoadingContent>
      <LoadingTitle className="merri">Setting things up...</LoadingTitle>
      <LoadingList>
        {LOADING_STEPS.map((step, i) => {
          const isVisible = i === 0 || doneCount >= i;
          const isDone = doneCount > i;
          const isActive = isVisible && !isDone;

          return (
            <LoadingStep key={i} $visible={isVisible} $delay={i === 0 ? 0 : 0.15}>
              <LoadingIndicator $done={isDone}>
                {isDone ? (
                  <CheckMark className="fas fa-check" />
                ) : (
                  <Spinner />
                )}
              </LoadingIndicator>
              <LoadingStepText>
                <LoadingLabel $done={isDone}>{step.label}</LoadingLabel>
                {step.subtext && isVisible && (
                  <LoadingSubtext $visible={isActive || isDone}>{step.subtext}</LoadingSubtext>
                )}
              </LoadingStepText>
            </LoadingStep>
          );
        })}
      </LoadingList>
    </LoadingContent>
  );
}

/* ── Complete View ────────────────────────────────────────── */

function CompleteView() {
  const { data, activeSessionId, collapseOverlay } = useWizard();
  const gen = data.generalInfo;
  const pricing = data.pricing;
  const classes = (gen.classes || []).filter((c) => c.trim());
  const [copied, setCopied] = useState(false);

  // Mark session as finished in localStorage
  useEffect(() => {
    if (!activeSessionId) return;
    try {
      const stored = localStorage.getItem("fnr-wizard-sessions");
      if (!stored) return;
      const sessions = JSON.parse(stored);
      const idx = sessions.findIndex((s) => s.id === activeSessionId);
      if (idx !== -1 && !sessions[idx].finished) {
        sessions[idx].finished = true;
        localStorage.setItem("fnr-wizard-sessions", JSON.stringify(sessions));
      }
    } catch {}
  }, [activeSessionId]);

  const signupCounts = useMemo(
    () => classes.map(() => Math.floor(Math.random() * 20) + 1),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const slug = (gen.programName || "program")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  const shareLink = `https://faithnetwork.com/signup/${slug}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const hasPricing = !!pricing.baseFee;
  const discounts = [];
  if (pricing.earlyBirdPricing && pricing.earlyBirdFee) {
    const d = { label: "Early bird", price: `$${pricing.earlyBirdFee}` };
    if (pricing.earlyBirdFamilyCap && pricing.earlyBirdFamilyCapValue) d.max = `$${pricing.earlyBirdFamilyCapValue}`;
    discounts.push(d);
  }
  if (pricing.latePricing && pricing.lateFee) {
    const d = { label: "Late pricing", price: `$${pricing.lateFee}` };
    if (pricing.lateFamilyCap && pricing.lateFamilyCapValue) d.max = `$${pricing.lateFamilyCapValue}`;
    discounts.push(d);
  }
  if (pricing.memberPricing && pricing.memberFee) {
    const d = { label: "Member", price: `$${pricing.memberFee}` };
    if (pricing.memberFamilyCap && pricing.memberFamilyCapValue) d.max = `$${pricing.memberFamilyCapValue}`;
    discounts.push(d);
  }
  if (pricing.catechistPricing && pricing.catechistFee) {
    const d = { label: "Catechist", price: `$${pricing.catechistFee}` };
    if (pricing.catechistFamilyCap && pricing.catechistFamilyCapValue) d.max = `$${pricing.catechistFamilyCapValue}`;
    discounts.push(d);
  }

  const fees = pricing.additionalFees || {};
  const classFees = classes
    .map((cls, i) => ({ label: cls, fee: fees[String(i)] }))
    .filter((cf) => cf.fee);
  const classFeeRows = classFees.map((cf) => ({ label: cf.label, price: `+$${cf.fee}` }));

  return (
    <CompleteContent>
      <CompleteHeader>
        <CompleteTitle className="merri">{gen.programName}</CompleteTitle>
        <CompleteSubtitle className="raleway">
          Your program is live and ready for families to sign up.
        </CompleteSubtitle>
      </CompleteHeader>

      <CompleteSection>
        <ShareLabel><i className="fas fa-link" /> Your Registration URL</ShareLabel>
        <ShareRow>
          <ShareLink>{shareLink}</ShareLink>
          <CopyButton onClick={handleCopy} $copied={copied}>
            <i className={`fas ${copied ? "fa-check" : "fa-copy"}`} />
          </CopyButton>
          <EmailBtnWrap>
            <FnrShineButton onClick={() => {}} borderRadius={100}>
              <EmailBtnInside>
                <i className="fas fa-envelope" /> Email this link
              </EmailBtnInside>
            </FnrShineButton>
          </EmailBtnWrap>
        </ShareRow>
      </CompleteSection>

      <ManageLink onClick={collapseOverlay}>
        <ManageInner>
          <i className="fas fa-arrow-up-right-from-square" /> Manage the signup
        </ManageInner>
      </ManageLink>

      <BottomColumns>
        <CompleteSection>
          <SectionLabel>Groups</SectionLabel>
          <ClassList>
            <ClassRow onClick={collapseOverlay}>
              <ClassLeft>
                <ClassIcon className="fas fa-layer-group" />
                <ClassLink>{gen.programName}</ClassLink>
              </ClassLeft>
            </ClassRow>
            {classes.map((cls, i) => (
              <ClassRow key={i} $indent onClick={collapseOverlay}>
                <ClassLeft>
                  <ClassIcon className="fas fa-users" />
                  <ClassLink>{cls}</ClassLink>
                </ClassLeft>
                <ClassCount>{signupCounts[i]} signed up</ClassCount>
              </ClassRow>
            ))}
          </ClassList>
        </CompleteSection>

        {(hasPricing || classFeeRows.length > 0 || pricing.advancedPricingRequested) && (
          <div>
            {hasPricing && (
              <PricingSummary>
                <PriceMain>${pricing.baseFee} <PriceUnit>per student</PriceUnit></PriceMain>
                {pricing.familyCap && pricing.familyCapValue && (
                  <PriceSub>Family cap: ${pricing.familyCapValue}</PriceSub>
                )}
              </PricingSummary>
            )}
            {classFeeRows.length > 0 && (
              <>
                <PriceDivider />
                {classFeeRows.map((cf) => (
                  <DiscountRow key={cf.label}>
                    <DiscountLabel>{cf.label}</DiscountLabel>
                    <DiscountPrice>{cf.price}</DiscountPrice>
                  </DiscountRow>
                ))}
              </>
            )}
            {discounts.length > 0 && (
              <>
                <PriceDivider />
                {discounts.map((d) => (
                  <DiscountEntry key={d.label}>
                    <DiscountRow>
                      <DiscountLabel>{d.label}</DiscountLabel>
                      <DiscountPrice>{d.price}</DiscountPrice>
                    </DiscountRow>
                    {d.max && <DiscountMax>Max {d.max}</DiscountMax>}
                  </DiscountEntry>
                ))}
              </>
            )}
            {pricing.advancedPricingRequested && (
              <HappinessNotice style={{ marginTop: 20 }}>
                <HappinessIcon className="fas fa-headset" />
                <HappinessText>
                  A happiness engineer will reach out to help configure your advanced pricing options.
                </HappinessText>
              </HappinessNotice>
            )}
          </div>
        )}
      </BottomColumns>
    </CompleteContent>
  );
}

/* ── Router ──────────────────────────────────────────────── */

export default function Confirmation({ subStep }) {
  if (subStep === 1) return <LoadingView />;
  if (subStep === 2) return <CompleteView />;
  return <ConfirmationView />;
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h2`
  font-size: 32px;
  margin: 0;
  color: #182941;
`;

const CardsCenter = styled.div`
  padding: 16px 0;
`;

const FeatureCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const FeatureCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 22px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const FeatureIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${ACCENT_COLOR};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const FeatureIcon = styled.i`
  font-size: 19px;
  color: #fff;
`;

const FeatureTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const FeatureLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #182941;
`;

const FeatureDesc = styled.div`
  font-size: 15px;
  color: #6b7280;
  line-height: 1.45;
`;

/* Details accordion */

const DetailsClip = styled.div`
  height: ${(p) => p.$height}px;
  overflow: hidden;
  transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const DetailsWrap = styled.div``;

const DetailsToggle = styled.button`
  appearance: none;
  border: none;
  background: none;
  font-size: 15px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  padding: 10px 14px;
  border-radius: 100px;
  font-family: inherit;
  display: block;
  margin: 0 auto;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: ${ACCENT_COLOR};
  }

  &:active {
    background: rgba(0, 0, 0, 0.07);
  }
`;

const DetailsToggleInner = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.15s;

  ${DetailsToggle}:active & {
    transform: scale(0.95);
  }
`;

const DetailsChevron = styled.i`
  font-size: 12px;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${(p) => (p.$open ? "90deg" : "0deg")});
`;

const DetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px 0 24px;
  width: 100%;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const DetailIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(1, 96, 153, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const DetailIcon = styled.i`
  font-size: 18px;
  color: ${ACCENT_COLOR};
`;

const DetailText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const DetailTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #182941;
`;

const DetailSummary = styled.div`
  font-size: 15px;
  color: #6b7280;
  line-height: 1.45;
`;

const EditLink = styled.button`
  appearance: none;
  border: none;
  background: none;
  color: ${ACCENT_COLOR};
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;
  padding: 6px 12px 6px 10px;
  border-radius: 100px;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 2px;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #e5e7eb;
  }

  &:active {
    background: ${ACCENT_COLOR};
    color: #fff;
  }
`;

const EditLinkInner = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  transition: transform 0.15s;

  ${EditLink}:active & {
    transform: scale(0.88);
  }
`;

/* ── Loading styles ──────────────────────────────────────── */

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 32px;
`;

const LoadingTitle = styled.h2`
  font-size: 28px;
  margin: 0;
  color: #182941;
`;

const LoadingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 400px;
`;

const LoadingStep = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? 0 : 12)}px);
  animation: ${(p) => (p.$visible ? fadeSlideIn : "none")} 0.35s ease-out both;
  animation-delay: ${(p) => p.$delay || 0}s;
`;

const LoadingIndicator = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${(p) => (p.$done ? "#22c55e" : "rgba(1, 96, 153, 0.08)")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.3s;
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2.5px solid #d1d5db;
  border-top-color: ${ACCENT_COLOR};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const CheckMark = styled.i`
  font-size: 16px;
  color: #fff;
`;

const LoadingStepText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const LoadingLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${(p) => (p.$done ? "#6b7280" : "#182941")};
  transition: color 0.3s;
`;

const LoadingSubtext = styled.div`
  font-size: 14px;
  color: #9ca3af;
  line-height: 1.4;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 0.3s;
`;

/* ── Complete styles ─────────────────────────────────────── */

const CompleteContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  max-width: 680px;
  width: 100%;
  margin: 0 auto;
`;

const CompleteHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
`;

const CompleteTitle = styled.h2`
  font-size: 28px;
  margin: 0;
  color: #182941;
`;

const CompleteSubtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0;
`;

const CompleteSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  text-align: center;
`;

const ShareLabel = styled.label`
  font-size: 16px;
  color: #4b5563;
  padding-left: 20px;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    font-size: 13px;
  }
`;

const ShareRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 100px;
  padding: 4px 4px 4px 16px;
`;

const ShareLink = styled.div`
  flex: 1;
  font-size: 14px;
  color: ${ACCENT_COLOR};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const CopyButton = styled.button`
  appearance: none;
  border: none;
  background: ${(p) => (p.$copied ? "#22c55e" : "rgba(1, 96, 153, 0.08)")};
  color: ${(p) => (p.$copied ? "#fff" : ACCENT_COLOR)};
  font-size: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s, transform 0.15s;

  &:hover {
    background: ${(p) => (p.$copied ? "#16a34a" : "rgba(1, 96, 153, 0.15)")};
  }

  &:active {
    transform: scale(0.9);
  }
`;

const EmailBtnWrap = styled.div`
  background: ${ACCENT_COLOR};
  border-radius: 100px;
  flex-shrink: 0;
`;

const EmailBtnInside = styled.div`
  font-size: 16px;
  color: #fff;
  text-shadow: rgba(0, 0, 0, 0.2) 1px 1px 3px;
  padding: 10px 22px;
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    font-size: 18px;
  }
`;

const ManageLink = styled.button`
  appearance: none;
  border: none;
  background: none;
  color: ${ACCENT_COLOR};
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  padding: 8px 16px;
  border-radius: 100px;
  cursor: pointer;
  align-self: flex-start;
  margin-top: -18px;
  transition: background 0.15s;

  &:hover {
    background: rgba(1, 96, 153, 0.06);
  }

  &:active {
    background: rgba(1, 96, 153, 0.12);
  }
`;

const ManageInner = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.15s;

  ${ManageLink}:active & {
    transform: scale(0.95);
  }
`;

const BottomColumns = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 28px;
  align-items: start;
`;

const ClassList = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const ClassRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  padding-left: ${(p) => (p.$indent ? "32px" : "16px")};
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f9fafb;
  }

  & + & {
    border-top: 1px solid #f3f4f6;
  }

  &:nth-child(even) {
    background: #f9fafb;
  }
`;

const ClassLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const ClassIcon = styled.i`
  font-size: 14px;
  color: #9ca3af;
`;

const ClassLink = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${ACCENT_COLOR};
`;

const ClassCount = styled.span`
  font-size: 13px;
  color: #9ca3af;
  flex-shrink: 0;
`;

const PricingSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PriceMain = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #182941;
`;

const PriceUnit = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #6b7280;
`;

const PriceSub = styled.div`
  font-size: 13px;
  color: #9ca3af;
`;

const PriceDivider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 8px 0;
`;

const DiscountEntry = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const DiscountRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
`;

const DiscountMax = styled.div`
  font-size: 12px;
  color: #9ca3af;
  text-align: right;
`;

const DiscountLabel = styled.span`
  font-size: 14px;
  color: #6b7280;
`;

const DiscountPrice = styled.span`
  font-size: 14px;
  color: #374151;
`;

const HappinessNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  padding: 16px;
`;

const HappinessIcon = styled.i`
  font-size: 20px;
  color: ${ACCENT_COLOR};
  margin-top: 1px;
  flex-shrink: 0;
`;

const HappinessText = styled.div`
  font-size: 14px;
  color: #1e40af;
  line-height: 1.45;
`;
